import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../store/toast/toastSlice";
import {
  createCompanyBranch,
  fetchCompanyBranches,
  updateBranch,
} from "../../../../services/api/branch";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@mui/material";
import BaseSelect from "../../../../components/ui/BaseSelect";
import { CurrencySymbol } from "../../../../utils/enum";
import { createBankAccount, updateBankAccount } from "../../../../services/api/bankAccount";

const getValidationSchema = (isEdit) =>
  yup.object({
    accountHolderName: yup.string().required("Enter account holder name"),

    bankName: yup.string().required("Enter bank name"),

    bankAddress: yup.string().optional(),

    accountNumber: yup.string().required("Enter account number"),

    branchId: yup.number().required("Select branch"),

    iban: yup.string().optional(),

    swiftCode: yup.string().optional(),

    accountType: yup.string().required("Select account type"),

    currency: yup.string().required("Select currency"),

    routingNumber: yup.string().optional(),

    sortCode: yup.string().optional(),

    bsb: yup.string().optional(),

    institutionNumber: yup.string().optional(),

    transitNumber: yup.string().optional(),

    bankCode: yup.string().optional(),
  });

const AddEditBankAccountModal = ({
  open,
  close,
  setBankAccountsList,
  selectedBankAccount,
  setSelectedBankAccount,
  isEditMode,
  setIsEditMode,
}) => {
  const dispatch = useDispatch();

  const {
    data: branchesData,
    isPending: isFetchCompanyBranchesApiLoading,
    error: isFetchCompanyBranchesApiError,
    refetch,
  } = useQuery({
    queryKey: ["branchesData"],
    queryFn: () => fetchCompanyBranches(),
  });
  const {
    isPending: isCreateBankAccountApiLoading,
    error: isCreateBankAccountApiError,
    data: newBankAccountData,
    mutateAsync: createBankAccountMutation,
  } = useMutation({
    mutationFn: createBankAccount,
    enabled: false,
  });

  const {
    isPending: isUpdateBankAccountApiLoading,
    error: isUpdateBankAccountApiError,
    data: updatedAccount,
    mutateAsync: updateBankAccountMutation,
  } = useMutation({
    mutationFn: updateBankAccount,
    enabled: false,
  });

  const allCurrencies = Object.keys(CurrencySymbol);
  const formattedBranchData =
    branchesData?.data?.data?.map((branch) => ({
      label: branch?.name,
      value: branch?.id,
    })) || [];

  const formik = useFormik({
    initialValues: {
      accountHolderName: selectedBankAccount?.accountHolderName || "",
      bankName: selectedBankAccount?.bankName || "",
      accountNumber: selectedBankAccount?.accountNumber || "",
      bankAddress: selectedBankAccount?.bankAddress || "",
      iban: selectedBankAccount?.iban || "",
      swiftCode: selectedBankAccount?.swiftCode || "",
      accountType: selectedBankAccount?.accountType || "",
      currency: selectedBankAccount?.currency || "",
      routingNumber: selectedBankAccount?.routingNumber || "",
      sortCode: selectedBankAccount?.sortCode || "",
      bsb: selectedBankAccount?.bsb || "",
      institutionNumber: selectedBankAccount?.institutionNumber || "",
      transitNumber: selectedBankAccount?.transitNumber || "",
      bankCode: selectedBankAccount?.bankCode || "",
      branchId: selectedBankAccount?.branchId || null,
    },
    validationSchema: getValidationSchema(!!selectedBankAccount),
    onSubmit: (values) => {
      handleSubmitButton();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleSubmitButton = () => {
    if (selectedBankAccount) {
      editBranch();
      setIsEditMode(false);
    } else {
      createNewBankAccount();
      setIsEditMode(false);
    }
  };

  const createNewBankAccount = async () => {
    try {
      const newBankAccount = await createBankAccountMutation(formik.values);
      setBankAccountsList((prevBankAccount) => [newBankAccount?.data, ...prevBankAccount]);
      dispatch(
        showToast({
          message: "Account added successfully",
          type: "success",
        }),
      );
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        }),
      );
    }
  };

  const editBranch = async () => {
    try {
      const payload = {
        id: selectedBankAccount?.id,
        ...formik.values,
      };
      const updatedBankAccount = await updateBankAccountMutation(payload);
      setBankAccountsList((prevBankAccount) =>
        prevBankAccount.map((account) =>
          account.id === updatedBankAccount?.data?.id
            ? { ...account, ...updatedBankAccount?.data }
            : account,
        ),
      );

      dispatch(
        showToast({
          message: "Details updated successfully",
          type: "success",
        }),
      );
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        }),
      );
    }
  };
  console.log(selectedBankAccount);
  return (
    <form>
      <BaseModal
        open={open}
        close={() => {
          close(false);
          setSelectedBankAccount(null);
          setIsEditMode(false);
        }}
        className="-top-[200px] overflow-auto"
        style={{ width: "1000px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">
            {selectedBankAccount
              ? isEditMode
                ? "Update Bank Account"
                : "Bank Account Details"
              : "Add Bank Account"}
          </h6>
        </div>
        <div className="px-8 py-4 grid grid-cols-3 gap-x-6 gap-y-4">
          <BaseTextField
            label={
              <span>
                Account Holder Name <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="accountHolderName"
            name="accountHolderName"
            value={formik.values.accountHolderName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.accountHolderName && Boolean(formik.errors.accountHolderName)}
            helperText={formik.touched.accountHolderName && formik.errors.accountHolderName}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            labelShrink={false}
            label={
              <span>
                Bank Name <span className="text-red-500">*</span>
              </span>
            }
            optional={false}
            isDefaultField={true}
            required
            fullWidth
            id="bankName"
            name="bankName"
            value={formik.values.bankName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.bankName && Boolean(formik.errors.bankName)}
            helperText={formik.touched.bankName && formik.errors.bankName}
            disabled={selectedBankAccount && !isEditMode}
          />
          <BaseSelect
            className="h-[39px]"
            required={true}
            label={
              <p className="mb-[7px] font-normal">
                Account Type <span className="text-red-500">*</span>
              </p>
            }
            labelShrink={false}
            name="accountType"
            placeholder="Select Account Type"
            options={[
              {
                label: "Savings",
                value: "SAVINGS",
              },
              {
                label: "Current",
                value: "CURRENT",
              },
              {
                label: "Checking",
                value: "CHECKING",
              },
            ]}
            value={formik.values.accountType}
            onChange={(option) => formik.setFieldValue("accountType", option.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.accountType && Boolean(formik.errors.accountType)}
            helperText={formik.touched.accountType && formik.errors.accountType}
            isClearable
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label={
              <span>
                Account Number <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="accountNumber"
            name="accountNumber"
            value={formik.values.accountNumber || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.accountNumber && Boolean(formik.errors.accountNumber)}
            helperText={formik.touched.accountNumber && formik.errors.accountNumber}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Bank Address"
            labelShrink={false}
            optional={false}
            id="bankAddress"
            name="bankAddress"
            value={formik.values.bankAddress || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.bankAddress && Boolean(formik.errors.bankAddress)}
            helperText={formik.touched.bankAddress && formik.errors.bankAddress}
            disabled={selectedBankAccount && !isEditMode}
          />
          <BaseSelect
            className="h-[39px]"
            required={true}
            label={
              <p className="mb-[7px] font-normal">
                Branch <span className="text-red-500">*</span>
              </p>
            }
            labelShrink={false}
            name="branchId"
            placeholder="Select Branch"
            options={formattedBranchData}
            value={formik.values.branchId}
            onChange={(option) => formik.setFieldValue("branchId", option.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.branchId && Boolean(formik.errors.branchId)}
            helperText={formik.touched.branchId && formik.errors.branchId}
            isClearable
            disabled={selectedBankAccount && !isEditMode}
          />
          <BaseSelect
            className="h-[39px]"
            required={true}
            label={
              <p className="mb-[7px] font-normal">
                Currency <span className="text-red-500">*</span>
              </p>
            }
            labelShrink={false}
            name="currency"
            placeholder="Select Currency"
            options={allCurrencies.map((currency) => ({
              label: currency,
              value: currency,
            }))}
            value={formik.values.currency}
            onChange={(option) => formik.setFieldValue("currency", option.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.currency && Boolean(formik.errors.currency)}
            helperText={formik.touched.currency && formik.errors.currency}
            isClearable
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="IBAN"
            labelShrink={false}
            optional={false}
            id="iban"
            name="iban"
            value={formik.values.iban || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.iban && Boolean(formik.errors.iban)}
            helperText={formik.touched.iban && formik.errors.iban}
            disabled={selectedBankAccount && !isEditMode}
          />
          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Swift Code"
            labelShrink={false}
            optional={false}
            id="swiftCode"
            name="swiftCode"
            value={formik.values.swiftCode || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.swiftCode && Boolean(formik.errors.swiftCode)}
            helperText={formik.touched.swiftCode && formik.errors.swiftCode}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Routing Number"
            labelShrink={false}
            optional={false}
            id="routingNumber"
            name="routingNumber"
            value={formik.values.routingNumber || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.routingNumber && Boolean(formik.errors.routingNumber)}
            helperText={formik.touched.routingNumber && formik.errors.routingNumber}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Sort Code"
            labelShrink={false}
            optional={false}
            id="sortCode"
            name="sortCode"
            value={formik.values.sortCode || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.sortCode && Boolean(formik.errors.sortCode)}
            helperText={formik.touched.sortCode && formik.errors.sortCode}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="BSB"
            labelShrink={false}
            optional={false}
            id="bsb"
            name="bsb"
            value={formik.values.bsb || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.bsb && Boolean(formik.errors.bsb)}
            helperText={formik.touched.bsb && formik.errors.bsb}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Institution Number"
            labelShrink={false}
            optional={false}
            id="institutionNumber"
            name="institutionNumber"
            value={formik.values.institutionNumber || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.institutionNumber && Boolean(formik.errors.institutionNumber)}
            helperText={formik.touched.institutionNumber && formik.errors.institutionNumber}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Transit Number"
            labelShrink={false}
            optional={false}
            id="transitNumber"
            name="transitNumber"
            value={formik.values.transitNumber || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.transitNumber && Boolean(formik.errors.transitNumber)}
            helperText={formik.touched.transitNumber && formik.errors.transitNumber}
            disabled={selectedBankAccount && !isEditMode}
          />

          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Bank Code"
            labelShrink={false}
            optional={false}
            id="bankCode"
            name="bankCode"
            value={formik.values.bankCode || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.bankCode && Boolean(formik.errors.bankCode)}
            helperText={formik.touched.bankCode && formik.errors.bankCode}
            disabled={selectedBankAccount && !isEditMode}
          />
        </div>

        <div
          className="d-flex content-center  bg-white py-6 mt-6"
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className=" px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => {
              close(false);
              setSelectedBankAccount(null);
              setIsEditMode(false);
            }}
          >
            Cancel
          </BaseButton>

          {isEditMode && (
            <BaseButton
              disabled={
                !formik.dirty ||
                !formik.isValid ||
                isCreateBankAccountApiLoading ||
                isUpdateBankAccountApiLoading
              }
              loaderSize="22px"
              isLoading={isCreateBankAccountApiLoading || isUpdateBankAccountApiLoading}
              color="primary"
              className="py-[5px] px-[20px] min-w-32"
              type="submit"
              onClick={formik.handleSubmit}
            >
              {selectedBankAccount ? "Update" : "Add"}
            </BaseButton>
          )}
        </div>
      </BaseModal>
    </form>
  );
};

export default AddEditBankAccountModal;
